/**
 * Shared utilities for Storybook play functions and interaction tests.
 * These helpers provide a consistent way to write testable interactions
 * without relying on deprecated testing libraries.
 */

/**
 * Custom assertion function that throws an error if the condition is false.
 * Use this instead of expect() from testing libraries.
 */
export function assert(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

/**
 * Creates a step wrapper that shows up as a distinct entry in Storybook's Interactions tab.
 * This makes it easier to identify which step failed during debugging.
 *
 * @param step - The step function from Storybook's play function context
 * @returns A doStep function that can be used to wrap test steps
 *
 * @example
 * ```typescript
 * MyStory.play = async ({ canvasElement, step }) => {
 *   const doStep = createDoStep(step);
 *
 *   await doStep('Find button', async () => {
 *     const button = canvasElement.querySelector('button');
 *     assert(button, 'Expected to find a button');
 *   });
 * };
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createDoStep(step: any): (label: string, fn: () => Promise<void> | void) => Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return typeof step === 'function'
    ? step
    : async (_label: string, fn: () => Promise<void> | void) => {
        await fn();
      };
}

/**
 * Custom waitFor utility for asynchronous assertions.
 * Polls the provided function until it succeeds or times out.
 *
 * @param fn - The function to execute (should throw if the condition is not met)
 * @param timeout - Maximum time to wait in milliseconds (default: 3000)
 *
 * @example
 * ```typescript
 * await waitFor(() => {
 *   const element = canvasElement.querySelector('.loaded');
 *   assert(element, 'Expected element to be loaded');
 * });
 * ```
 */
export async function waitFor(fn: () => void, timeout = 3000): Promise<void> {
  const start = Date.now();
  let lastError: Error | null = null;
  while (Date.now() - start < timeout) {
    try {
      fn();
      return;
    } catch (e) {
      lastError = e as Error;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }
  throw lastError;
}

/**
 * Type definition for spy functions used to verify callback invocations.
 * These functions track how many times they've been called.
 */
export type SpyFn = ((...args: unknown[]) => void) & { callCount: number };

/**
 * Creates a spy function that tracks the number of times it's been called.
 * Use this for callback props in stories where you need to verify they were invoked.
 *
 * @returns A function that increments its callCount each time it's called
 *
 * @example
 * ```typescript
 * MyStory.args = {
 *   onClick: createSpy()
 * };
 *
 * MyStory.play = async ({ args, canvasElement, step }) => {
 *   const doStep = createDoStep(step);
 *   const onClick = args.onClick as SpyFn;
 *
 *   // Reset before interaction
 *   onClick.callCount = 0;
 *
 *   // Perform interaction
 *   const button = canvasElement.querySelector('button');
 *   button.click();
 *
 *   // Verify
 *   await doStep('Verify onClick was called', async () => {
 *     assert(onClick.callCount > 0, 'Expected onClick to be called');
 *   });
 * };
 * ```
 */
export function createSpy(): SpyFn {
  const spy = ((..._args: unknown[]) => {
    spy.callCount += 1;
  }) as SpyFn;
  spy.callCount = 0;
  return spy;
}
